import axios from 'axios';

const ADMIN_URL = process.env.VUE_APP_ADMIN_URL;

function getHeaders() {
  const token = JSON.parse(localStorage.getItem('accessToken'));
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// Los endpoints nuevos responden { error: { code, message } }; se conserva el code.
function bankDetailsError(error) {
  const body = (error.response && error.response.data) || {};
  const detail = body.error || body;
  return {
    status: 'error',
    code: detail.code,
    message: detail.message ?? error.message,
    httpCode: error.response?.status
  };
}

// Red Pawer (022-red-pawer): el 422 NETWORK_PROFILE_INCOMPLETE trae `missing` con los
// datos que faltan; se lee del body o de body.error por si el backend lo anida.
function networkProfileError(error) {
  const body = (error.response && error.response.data) || {};
  const detail = body.error || body;
  return {
    status: 'error',
    code: detail.code ?? detail.message,
    message: detail.message ?? error.message,
    httpCode: error.response?.status,
    missing: Array.isArray(detail.missing) ? detail.missing : []
  };
}

// El contrato muestra el perfil sin envoltorio; se acepta también `{ data }` como el
// resto de los endpoints de droopy-clinics.
function unwrapNetworkProfile(body) {
  return body && body.data !== undefined ? body.data : body;
}

// Todos los métodos siguen el mismo contrato que el resto de src/services/:
// { status: 'ok', data } en éxito, { status: 'error', message, httpCode } en error —
// httpCode viene de la respuesta del backend (server-configuration.middleware.ts siempre
// responde { status: 'error', message, httpCode }), para que quien llame pueda distinguir
// 404/409 de otros errores sin parsear el mensaje (014-clinic-onboarding-ui).
export default class ClinicsService {
  static async getOwnServicesForManagement(clinicId) {
    try {
      const response = await axios.get(
        `${ADMIN_URL}/clinics/${clinicId}/services/own/manage`,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  static async getServicePricing(clinicId) {
    try {
      const response = await axios.get(
        `${ADMIN_URL}/clinics/${clinicId}/services/pricing`,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  static async createOwnService(clinicId, payload) {
    try {
      const response = await axios.post(
        `${ADMIN_URL}/clinics/${clinicId}/services/own`,
        payload,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  static async updateOwnService(clinicId, id, payload) {
    try {
      const response = await axios.patch(
        `${ADMIN_URL}/clinics/${clinicId}/services/own/${id}`,
        payload,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  static async createCatalogService(clinicId, payload) {
    try {
      const response = await axios.post(
        `${ADMIN_URL}/clinics/${clinicId}/services/catalog`,
        payload,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Carga masiva todo-o-nada de variantes de catálogo (spec 105 de pawer-backend). Un 422
  // trae además `errors: [{ index, code, serviceId? }]`, con `index` relativo a `items`.
  static async createCatalogServicesBulk(clinicId, items) {
    try {
      const response = await axios.post(
        `${ADMIN_URL}/clinics/${clinicId}/services/catalog/bulk`,
        { items },
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status,
        errors: error.response?.data?.errors ?? []
      };
    }
  }

  // Lista las clínicas con staff asociado ("clínicas Droopy"). Sin paginación —
  // el backend siempre devuelve el listado completo (puede ser []).
  // `excludeDemo` quita Pawer Clinic (clínica de demos); solo lo usan las métricas PetMed.
  static async getDroopyClinics({ excludeDemo = false } = {}) {
    try {
      const config = { headers: getHeaders() };
      if (excludeDemo) config.params = { excludeDemo: true };
      const response = await axios.get(`${ADMIN_URL}/droopy-clinics`, config);
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Edita nombre y/o RUT de una clínica Droopy. `changes` = { name?, rut? }: solo se envía lo
  // que cambió (el backend exige al menos uno). El email del administrador no se edita: es su
  // login de Cognito. Un 409 trae code CLINIC_RUT_TAKEN (el RUT ya es de otra clínica).
  static async updateDroopyClinic(clinicId, changes) {
    try {
      const response = await axios.patch(`${ADMIN_URL}/droopy-clinics/${clinicId}`, changes, {
        headers: getHeaders()
      });
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        code: error.response?.data?.code,
        httpCode: error.response?.status
      };
    }
  }

  // Habilita/deshabilita una clínica Droopy: fija el estado de la cuenta de su administrador.
  // `status` es explícito ('active' | 'inactive'), no un toggle; responde la clínica actualizada.
  static async setDroopyClinicStatus(clinicId, status) {
    try {
      const response = await axios.patch(
        `${ADMIN_URL}/droopy-clinics/${clinicId}/status`,
        { status },
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Genera una contraseña temporal nueva y reenvía el correo de bienvenida (contrato 085 de
  // pawer-backend). Solo aplica si el administrador nunca activó su cuenta; la respuesta
  // trae { clinicId, emailSent } y nunca la contraseña.
  static async resendClinicAdminTempPassword(clinicId) {
    try {
      const response = await axios.post(
        `${ADMIN_URL}/droopy-clinics/${clinicId}/administrator/reset-temporary-password`,
        {},
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  /**
   * Reads the Red Pawer profile of a Droopy clinic (022-red-pawer).
   * Without a saved profile the backend returns `type: null`, empty fields and
   * `commune` prefilled from `clinics.county`; 404 if it is not a Droopy clinic.
   * @param {string} clinicId
   * @returns {Promise<{status: 'ok', data: Object} | {status: 'error', message: string, code?: string, httpCode?: number}>}
   */
  static async getNetworkProfile(clinicId) {
    try {
      const response = await axios.get(
        `${ADMIN_URL}/droopy-clinics/${clinicId}/network-profile`,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: unwrapNetworkProfile(response.data) };
    } catch (error) {
      return networkProfileError(error);
    }
  }

  /**
   * Saves the Red Pawer profile of a Droopy clinic. If the profile was visible and is
   * now incomplete, the backend hides it and the returned profile carries `missing`.
   * @param {string} clinicId
   * @param {{type: 'clinic'|'home_visit', whatsapp: string|null, address: string|null,
   *   commune: {code: string, name: string}|null, homeVisitCommunes: {code: string, name: string}[]}} payload
   * @returns {Promise<{status: 'ok', data: Object} | {status: 'error', message: string, code?: string, httpCode?: number}>}
   */
  static async updateNetworkProfile(clinicId, payload) {
    try {
      const response = await axios.put(
        `${ADMIN_URL}/droopy-clinics/${clinicId}/network-profile`,
        payload,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: unwrapNetworkProfile(response.data) };
    } catch (error) {
      return networkProfileError(error);
    }
  }

  /**
   * Shows or hides a Droopy clinic in the app's Red Pawer. A 422
   * `NETWORK_PROFILE_INCOMPLETE` comes back with `missing` (the fields still needed).
   * @param {string} clinicId
   * @param {boolean} visibleInApp
   * @returns {Promise<{status: 'ok', data: Object} | {status: 'error', message: string, code?: string, httpCode?: number, missing: string[]}>}
   */
  static async setNetworkVisibility(clinicId, visibleInApp) {
    try {
      const response = await axios.patch(
        `${ADMIN_URL}/droopy-clinics/${clinicId}/network-profile/visibility`,
        { visibleInApp },
        { headers: getHeaders() }
      );
      return { status: 'ok', data: unwrapNetworkProfile(response.data) };
    } catch (error) {
      return networkProfileError(error);
    }
  }

  static async updateServicePricing(clinicId, id, pricing) {
    try {
      const response = await axios.patch(
        `${ADMIN_URL}/clinics/${clinicId}/services/${id}/pricing`,
        { pricing },
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Lista todos los clinic-services de una clínica (propios y vinculados a catálogo) con
  // sus servicios Pawer relacionados en un solo request — `pawerServices: []` significa
  // "servicio propio" (sin vínculo), y puede tener más de un elemento (combo/paquete).
  static async getClinicServicesPawerMapping(clinicId) {
    try {
      const response = await axios.get(
        `${ADMIN_URL}/clinics/${clinicId}/services/pawer-mapping`,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  static async renameClinicService(clinicId, id, title) {
    try {
      const response = await axios.patch(
        `${ADMIN_URL}/clinics/${clinicId}/services/${id}/title`,
        { title },
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Aplica a cualquier clinic-service (propio o vinculado a catálogo) — un solo endpoint,
  // a diferencia de updateOwnService (que solo cubre servicios propios).
  static async updateClinicServiceVisibility(clinicId, id, visible) {
    try {
      const response = await axios.patch(
        `${ADMIN_URL}/clinics/${clinicId}/services/${id}/visible`,
        { visible },
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Vincula un servicio Pawer adicional a un clinic-service ya existente. No es
  // idempotente — el backend responde 409 si ese vínculo ya existía.
  static async linkClinicServicePawerService(clinicId, id, serviceId) {
    try {
      const response = await axios.post(
        `${ADMIN_URL}/clinics/${clinicId}/services/${id}/pawer-links`,
        { serviceId },
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Quita un único vínculo puntual con un servicio Pawer. Si era el único vínculo, el
  // clinic-service pasa a comportarse como "propio" (pawerServices: []).
  static async unlinkClinicServicePawerService(clinicId, id, serviceId) {
    try {
      const response = await axios.delete(
        `${ADMIN_URL}/clinics/${clinicId}/services/${id}/pawer-links/${serviceId}`,
        { headers: getHeaders() }
      );
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Veterinarios (dr-clinics) de una clínica, sin filtrar por estado — el filtro de activos
  // lo hace quien llama (021-petmed-droopy-attendance, research Decisión 4).
  static async getClinicDoctors(clinicId) {
    try {
      const response = await axios.get(`${ADMIN_URL}/clinics/${clinicId}/doctors`, {
        headers: getHeaders()
      });
      return { status: 'ok', data: response.data.data.doctors };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Servicios de la clínica con la cobertura ya resuelta para la membresía (serviceId Pawer,
  // cupos, carencia, montos sugeridos). `species` es opcional y activa categoryOptions.
  // `excludeServiceRecordId` (opcional, 024 FR-023): el registro en edición no cuenta en los cupos.
  static async getClinicServicesForMembership(clinicId, membershipId, species, excludeServiceRecordId) {
    try {
      let url = `${ADMIN_URL}/clinics/${clinicId}/services/for-membership?membershipId=${membershipId}`;
      if (species) {
        url = url + `&species=${species}`;
      }
      if (excludeServiceRecordId) {
        url = url + `&excludeServiceRecordId=${excludeServiceRecordId}`;
      }
      const response = await axios.get(url, { headers: getHeaders() });
      return { status: 'ok', data: response.data.data.services };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }

  // Datos de transferencia de la clínica (125-clinic-billing-admin). El GET trae el número de
  // cuenta enmascarado (****5678) y `bankName`; `bankId` es el UUID del banco.
  static async getBankDetails(clinicId) {
    try {
      const response = await axios.get(`${ADMIN_URL}/clinics/${clinicId}/bank-details`, {
        headers: getHeaders()
      });
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return bankDetailsError(error);
    }
  }

  // payload: { bankId, bankAccountType, bankAccountNumber, bankAccountHolder, paymentEmail } — todos obligatorios.
  static async updateBankDetails(clinicId, payload) {
    try {
      const response = await axios.put(`${ADMIN_URL}/clinics/${clinicId}/bank-details`, payload, {
        headers: getHeaders()
      });
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return bankDetailsError(error);
    }
  }

  // Crea en una transacción invoice → attendance → services-record (operationType 'petmed',
  // con attendanceId). Payload según specs/021-petmed-droopy-attendance/contracts.
  static async createAttendance(clinicId, payload) {
    try {
      const response = await axios.post(`${ADMIN_URL}/clinics/${clinicId}/attendances`, payload, {
        headers: getHeaders()
      });
      return { status: 'ok', data: response.data.data };
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message ?? error.message,
        httpCode: error.response?.status
      };
    }
  }
}
